import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { MeResponse, ImportReport, DashboardResponse, StaffListResponse, StaffMember } from '@pb26/shared';
import { api, apiUpload } from './client';
import { isVkMiniAppHost } from '../utils/vkBridge';
import bridge from '@vkontakte/vk-bridge';

async function initVkBridge(): Promise<void> {
  if (import.meta.env.DEV && !isVkMiniAppHost()) return;
  await Promise.race([
    bridge.send('VKWebAppInit'),
    new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('VKWebAppInit timeout')), 5000),
    ),
  ]);
}

export function useVkInit() {
  return useMutation({
    mutationFn: async () => {
      try {
        await initVkBridge();
      } catch {
        if (!import.meta.env.DEV) throw new Error('VK Bridge init failed');
      }
      const params = new URLSearchParams(window.location.search);
      const launchParams = params.toString() || 'vk_user_id=1000001';
      return api<{ vkUserId: number; role: string; needsVerify: boolean }>(
        '/api/auth/vk',
        {
          method: 'POST',
          body: JSON.stringify({ launchParams }),
        },
      );
    },
  });
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/api/me'),
    enabled,
    retry: false,
  });
}

export function useVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { idPervye: number; email: string }) =>
      api<{ ok: boolean; licenseLogin?: string }>('/api/auth/verify', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLicense(enabled = true) {
  return useQuery({
    queryKey: ['license'],
    queryFn: () => api<{ login: string; password: string }>('/api/profile/license'),
    enabled,
    retry: false,
  });
}

export function useSimulator() {
  return useQuery({
    queryKey: ['simulator'],
    queryFn: () =>
      api<{
        downloadUrl: string | null;
        version: string | null;
        releasedAt: string | null;
      }>('/api/simulator'),
  });
}

export function useMaps() {
  return useQuery({
    queryKey: ['maps'],
    queryFn: () =>
      api<{
        maps: Array<{
          id: number;
          title: string;
          description: string | null;
          downloadUrl: string;
        }>;
      }>('/api/maps'),
  });
}

export function useDashboard() {
  return useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api<DashboardResponse>('/api/admin/dashboard'),
  });
}

export function useParticipants(page: number, search: string) {
  return useQuery({
    queryKey: ['admin', 'participants', page, search],
    queryFn: () =>
      api<{
        participants: Array<Record<string, unknown>>;
        total: number;
      }>(`/api/admin/participants?page=${page}&limit=50&search=${encodeURIComponent(search)}`),
  });
}

export function useImportJob(jobId: string | null) {
  return useQuery({
    queryKey: ['import', jobId],
    queryFn: () =>
      api<{ status: string; report: ImportReport | null }>(
        `/api/admin/import/${jobId}`,
      ),
    enabled: !!jobId,
    refetchInterval: (q) =>
      q.state.data?.status === 'processing' ? 1000 : false,
  });
}

export function useImportParticipants() {
  return useMutation({
    mutationFn: (file: File) =>
      apiUpload<{ jobId: string }>('/api/admin/participants/import', file),
  });
}

export function useAdminMaps() {
  return useQuery({
    queryKey: ['admin', 'maps'],
    queryFn: () => api<{ maps: Array<Record<string, unknown>> }>('/api/admin/maps'),
  });
}

export function useStaff() {
  return useQuery({
    queryKey: ['admin', 'staff'],
    queryFn: () => api<StaffListResponse>('/api/admin/staff'),
  });
}

export function useCreateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { vkUserId: number; role: 'admin' | 'viewer'; fullName?: string }) =>
      api<{ staff: StaffMember }>('/api/admin/staff', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'staff'] }),
  });
}

export function useUpdateStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id: number;
      role?: 'admin' | 'viewer';
      fullName?: string;
    }) =>
      api<{ staff: StaffMember }>(`/api/admin/staff/${body.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ role: body.role, fullName: body.fullName }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'staff'] }),
  });
}

export function useDeleteStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api<{ ok: boolean }>(`/api/admin/staff/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'staff'] }),
  });
}
